import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { MapPin, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { geocodeAddress, reverseGeocode } from '@/lib/apis';
import { businessLocationsQueryOptions } from '@/lib/query-options';
import { radiusUnitAtom, type RadiusUnit } from '@/store';
import {
  MIN_RADIUS_KM,
  MAX_RADIUS_KM,
  DEFAULT_RADIUS_KM,
} from '@/lib/maps/config';
import type { GeocodeResultRead } from '@/lib/types';

// Lazy-load the Leaflet map so the ~50 KB bundle only ships when the dialog opens.
// Using the module's default export to keep the React.lazy contract simple
// (the named-export indirection tripped Vite HMR with "render2 is not a function").
const MapPreview = lazy(() => import('./map-preview'));

export interface RadiusFormValue {
  lat: number;
  lng: number;
  radiusKm: number;
  countryCode: string | null;
  formattedAddress: string;
  // #5497: unit this radius was entered in; persisted so the table/list show it the
  // same way after reload. radiusKm stays canonical; absent = km.
  unit?: RadiusUnit;
}

interface RadiusFormProps {
  businessId: number;
  /** Submit handler. Disabled state is managed internally; this fires only when the form is valid. */
  onSubmit?: (value: RadiusFormValue) => void | Promise<void>;
  /** Live change handler. Fires on every state update — receives the current value
   *  when valid, or null when the form is incomplete / invalid. Lets a parent
   *  drive its own submit button instead of relying on the form's internal one. */
  onChange?: (value: RadiusFormValue | null) => void;
  /** Whether the parent's submit is in-flight. Disables inputs to prevent double-fire. */
  submitting?: boolean;
  /** Submit-button copy. Defaults to "Add location" but #4384 (bid modifiers) will pass its own label. */
  submitLabel?: string;
  /** Hide the form's internal submit button. Use this when the parent owns the
   *  submit affordance and wires onChange instead. */
  hideSubmit?: boolean;
  /** Hide the internal map preview. Use when the parent renders its own map and
   *  draws the draft (from onChange) there instead. */
  hidePreview?: boolean;
  /** Pre-fill the form with an existing value (edit mode). The parent should
   *  also pass a stable `key` prop so the form remounts when the edit target
   *  changes. */
  initialValue?: RadiusFormValue | null;
  /** Optional content rendered alongside the radius row in a 50/50 grid. Used
   *  by the proximity bid-modifier surface to place the bid-adjustment input
   *  next to the radius input rather than stacked below. */
  radiusRowAside?: React.ReactNode;
}

const KM_PER_MILE = 1.609344;
const toKm = (value: number, unit: RadiusUnit): number =>
  unit === 'km' ? value : value * KM_PER_MILE;
const fromKm = (km: number, unit: RadiusUnit): number =>
  unit === 'km' ? km : km / KM_PER_MILE;

const MIN_RADIUS_MI = Math.ceil(MIN_RADIUS_KM / KM_PER_MILE);
const MAX_RADIUS_MI = Math.floor(MAX_RADIUS_KM / KM_PER_MILE);

/**
 * Address search + map preview + radius input. Submits a validated RadiusFormValue
 * to the parent dialog (or, in #4384, to the bid-modifier dialog).
 *
 * Validation rules enforced inside this component:
 * - Address must be geocoded before Submit is enabled
 * - Radius must be an integer within [MIN, MAX] for the selected unit
 * - country_code falls through from the geocoder; missing country_code does NOT block submit
 *   (radius targets can be country-less in Google Ads; the chokepoint handles the edge case)
 */
export function RadiusForm({
  businessId,
  onSubmit,
  onChange,
  submitting = false,
  submitLabel = 'Add location',
  hideSubmit = false,
  hidePreview = false,
  initialValue = null,
  radiusRowAside,
}: RadiusFormProps) {
  const [unit, setUnit] = useAtom(radiusUnitAtom);
  // Pre-fill the search input with the existing address in edit mode so the
  // user can see what they're editing. Auto-search is suppressed on first
  // render via `hasUserTyped` below to avoid re-geocoding the value we already
  // have a `selected` snapshot for.
  const [query, setQuery] = useState(initialValue?.formattedAddress ?? '');
  const [debouncedQuery] = useDebounce(query, 500);
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const addressInputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize the address textarea so pre-filled long addresses are fully
  // visible without the user clicking in to scroll.
  useEffect(() => {
    const el = addressInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [query]);
  const [matches, setMatches] = useState<GeocodeResultRead[] | null>(null);
  // Initialise selected + radius from initialValue when in edit mode so the
  // form shows the existing row instead of an empty state. Parent should remount
  // the form (via key) when the edit target changes.
  const [selected, setSelected] = useState<GeocodeResultRead | null>(
    initialValue
      ? {
          place_id: `initial-${initialValue.lat},${initialValue.lng}`,
          formatted_address: initialValue.formattedAddress,
          lat: initialValue.lat,
          lng: initialValue.lng,
          country_code: initialValue.countryCode,
          place_type: null,
        }
      : null,
  );
  const [radius, setRadius] = useState<string>(
    String(fromKm(initialValue ? initialValue.radiusKm : DEFAULT_RADIUS_KM, unit)),
  );
  const [radiusError, setRadiusError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Tell-on-click (#5857): set when submit is clicked without a geocoded address;
  // cleared when a suggestion is picked or the query changes.
  const [addressError, setAddressError] = useState<string | null>(null);

  const search = useMutation({
    mutationFn: () => geocodeAddress(businessId, query.trim(), 5),
    onSuccess: (results) => {
      setSearchError(results.length === 0 ? 'No matches found. Try a more specific address.' : null);
      setMatches(results);
      // Auto-pick if there's only one result so the user doesn't have to click.
      if (results.length === 1) {
        setSelected(results[0]);
        setAddressError(null);
      }
    },
    onError: (err: Error) => {
      setSearchError(err.message ?? 'Could not reach the geocoder. Please try again.');
      setMatches(null);
    },
  });

  const reverse = useMutation({
    mutationFn: ({ lat, lng }: { lat: number; lng: number }) =>
      reverseGeocode(businessId, lat, lng),
  });

  const handleMarkerDrag = async (lat: number, lng: number) => {
    // Build a synthetic selection from the new coordinates first so the marker
    // sticks even if the reverse geocode fails.
    const fallback: GeocodeResultRead = {
      place_id: `${lat},${lng}`,
      formatted_address: selected?.formatted_address ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
      lat,
      lng,
      country_code: selected?.country_code ?? null,
      place_type: null,
    };
    setSelected(fallback);
    try {
      const result = await reverse.mutateAsync({ lat, lng });
      if (result) setSelected(result);
    } catch {
      // Swallow — fallback already in place.
    }
  };

  // #4817: Enter confirms the search instead of inserting a newline (which the textarea otherwise
  // turns into a space). If matches are already listed, Enter picks the first; otherwise it fires
  // the geocode immediately rather than waiting on the debounce.
  const handleAddressKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (matches && matches.length > 0 && !selected) {
      setSelected(matches[0]);
      setAddressError(null);
    } else if (query.trim()) {
      setHasUserTyped(true);
      search.mutate();
    }
  };

  // #4817: spinner/feedback should cover the whole "search is happening" window — both the debounce
  // wait and the in-flight request — so the user knows something is working behind the scenes.
  const isSearching =
    search.isPending ||
    (hasUserTyped && query.trim() !== '' && !selected && query.trim() !== debouncedQuery.trim());

  // #4817: the map is always shown — when nothing is picked yet it previews the business's existing
  // radius locations as context markers so it's useful out of the gate.
  const { data: businessLocations } = useQuery(businessLocationsQueryOptions(businessId));
  const contextMarkers = useMemo(
    () =>
      (businessLocations ?? [])
        .filter((l) => l.variant === 'radius' && l.latitude != null && l.longitude != null && !l.dismissed_by_user)
        .map((l) => ({ lat: l.latitude as number, lng: l.longitude as number })),
    [businessLocations],
  );

  const validateRadius = (value: string): { km: number | null; error: string | null } => {
    if (!value.trim()) return { km: null, error: 'Radius is required.' };
    const n = Number(value);
    if (!Number.isFinite(n)) return { km: null, error: 'Radius must be a number.' };
    if (!Number.isInteger(n)) return { km: null, error: 'Radius must be a whole number.' };
    const min = unit === 'km' ? MIN_RADIUS_KM : MIN_RADIUS_MI;
    const max = unit === 'km' ? MAX_RADIUS_KM : MAX_RADIUS_MI;
    if (n < min) return { km: null, error: `Radius must be at least ${min} ${unit}.` };
    if (n > max) return { km: null, error: `Radius must be at most ${max} ${unit}.` };
    return { km: Math.round(toKm(n, unit)), error: null };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onSubmit) return;
    // Tell-on-click (#5857): mark each missing/invalid field instead of silently
    // disabling the submit button.
    const { km, error } = validateRadius(radius);
    setRadiusError(error);
    setAddressError(selected ? null : 'Pick an address from the suggestions');
    if (!selected || km === null) return;
    await onSubmit({
      lat: selected.lat,
      lng: selected.lng,
      radiusKm: km,
      countryCode: selected.country_code,
      formattedAddress: selected.formatted_address,
      unit,
    });
  };

  // Auto-search 500ms after the user stops typing. Backend caches in Redis +
  // throttles globally, so the cost of an occasional extra request is bounded.
  // Skip the search until the user has actually touched the input so the
  // pre-filled edit-mode value (which already has a `selected` snapshot) doesn't
  // trigger a redundant geocode round-trip on open.
  useEffect(() => {
    if (!hasUserTyped) return;
    const q = debouncedQuery.trim();
    if (!q) {
      setMatches(null);
      setSearchError(null);
      return;
    }
    search.mutate();
  }, [debouncedQuery, hasUserTyped]);

  // Live-state callback for parents that want to drive their own submit button
  // (#4530 — proximity bid modifier modal). Fires with null when the form is
  // incomplete / invalid so the parent can disable Save until the user picks a
  // valid address + radius.
  useEffect(() => {
    if (!onChange) return;
    if (!selected) {
      onChange(null);
      return;
    }
    const { km } = validateRadius(radius);
    if (km === null) {
      onChange(null);
      return;
    }
    onChange({
      lat: selected.lat,
      lng: selected.lng,
      radiusKm: km,
      countryCode: selected.country_code,
      formattedAddress: selected.formatted_address,
      unit,
    });
  }, [selected, radius, unit, onChange]);

  const handleUnitChange = (next: RadiusUnit) => {
    if (next === unit) return;
    // Convert the displayed value so the user's intent is preserved.
    const current = Number(radius);
    if (Number.isFinite(current)) {
      const km = toKm(current, unit);
      setRadius(String(Math.round(fromKm(km, next))));
    }
    setUnit(next);
    setRadiusError(null);
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Address search — auto-fires 500ms after the user stops typing. No
              explicit Search button: the affordance was confusing (users didn't
              know to click it). Spinner inside the input signals an in-flight lookup. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="radius-address">Address</Label>
          <FieldError message={addressError ?? undefined} />
        </div>
        <div className="relative">
          <Textarea
            id="radius-address"
            ref={addressInputRef}
            value={query}
            onChange={(e) => {
              // Strip newlines — the geocoder takes a single-line query and
              // pasted addresses sometimes carry line breaks.
              setQuery(e.target.value.replace(/\n+/g, ' '));
              setHasUserTyped(true);
              // Editing the address means the previous pick is stale — clear it so fresh matches
              // surface and the map reflects the new search (#4817).
              setSelected(null);
              setAddressError(null);
            }}
            onKeyDown={handleAddressKeyDown}
            placeholder="Start typing an address, then press Enter..."
            disabled={submitting}
            rows={1}
            error={!!addressError}
            className="resize-none min-h-[40px] pr-10 leading-snug overflow-hidden"
            data-testid="radius-address-input"
          />
          {!isSearching && <InputAdornment error={!!addressError} />}
          {isSearching && (
            <Loader2
              className="absolute right-3 top-3 h-4 w-4 animate-spin text-[#290BD9]"
              aria-label="Searching"
            />
          )}
        </div>
        {isSearching && (
          <p className="text-xs text-gray-500">Searching addresses…</p>
        )}
        {searchError && (
          <div className="flex items-start gap-2 rounded-md border border-l-4 border-l-[#FCA5A5] bg-[#FEE2E2] p-2 text-sm text-[#B91C1C]">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{searchError}</span>
          </div>
        )}
      </div>

      {/* Disambiguation list (only when 2+ matches and nothing picked yet) */}
      {matches && matches.length > 1 && !selected && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            Pick a match
          </div>
          <div className="max-h-[160px] overflow-y-auto rounded-md border bg-white">
            {matches.map((m) => (
              <button
                key={m.place_id}
                type="button"
                onClick={() => {
                  setSelected(m);
                  setAddressError(null);
                }}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                data-testid="radius-disambiguation-item"
              >
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                <span className="text-gray-900">{m.formatted_address}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Map preview — always shown (#4817). With a picked address it shows the draggable marker +
          radius circle; otherwise it previews the business's existing radius locations. Hidden
          when the parent owns the map (hidePreview) and draws the draft itself. */}
      {!hidePreview && (
        <>
          <Suspense
            fallback={<div className="h-[min(320px,38vh)] w-full animate-pulse rounded-md bg-gray-100" />}
          >
            <MapPreview
              lat={selected ? selected.lat : (contextMarkers[0]?.lat ?? 20)}
              lng={selected ? selected.lng : (contextMarkers[0]?.lng ?? 0)}
              radiusKm={validateRadius(radius).km ?? DEFAULT_RADIUS_KM}
              onMarkerDrag={selected ? handleMarkerDrag : undefined}
              markers={contextMarkers}
              showRadius={!!selected}
              zoom={selected || contextMarkers.length > 0 ? undefined : 2}
              className="h-[min(320px,38vh)] w-full overflow-hidden rounded-md border bg-gray-50"
            />
          </Suspense>
          {!selected && (
            <p className="text-xs text-gray-500">
              {contextMarkers.length > 0
                ? 'Showing your existing radius locations. Search an address above to add a new one.'
                : 'Search an address above to preview it on the map.'}
            </p>
          )}
        </>
      )}

      {/* Radius input + unit toggle. When an `radiusRowAside` slot is provided
              (e.g. by the proximity bid-modifier surface) the radius row sits in
              a 50/50 grid with the aside content. Otherwise the row caps at a
              narrow width since the input doesn't need to span the modal. */}
      <div className={radiusRowAside ? 'grid grid-cols-2 gap-4 items-end' : ''}>
        <div className={`flex items-end gap-3 ${radiusRowAside ? '' : 'max-w-[280px]'}`}>
          <div className="flex-1 space-y-2">
            <Label htmlFor="radius-value">Radius</Label>
            <Input
              id="radius-value"
              error={!!radiusError}
              type="number"
              inputMode="numeric"
              min={unit === 'km' ? MIN_RADIUS_KM : MIN_RADIUS_MI}
              max={unit === 'km' ? MAX_RADIUS_KM : MAX_RADIUS_MI}
              step={1}
              value={radius}
              onChange={(e) => {
                setRadius(e.target.value);
                setRadiusError(null);
              }}
              disabled={submitting}
              data-testid="radius-value-input"
            />
          </div>
          <div className="flex h-10 overflow-hidden rounded-md border border-[#290BD9]">
            {(['km', 'mi'] as const).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => handleUnitChange(u)}
                className={
                  u === unit
                    ? 'bg-[#290BD9] px-4 text-sm font-medium text-white'
                    : 'bg-white px-4 text-sm font-medium text-[#290BD9] hover:bg-[#EDE9FE]'
                }
                disabled={submitting}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        {radiusRowAside}
      </div>
      {radiusError && (
        <div>
          <FieldError message={radiusError} />
        </div>
      )}

      {!hideSubmit && (
        // #5507: pin the submit to the bottom of the scroll area so the form's fixed-height
        // map can't push it below the fold on shorter viewports.
        <div className="sticky bottom-0 z-10 flex justify-end border-t border-gray-100 bg-white pt-2">
          <Button
            type="submit"
            className="bg-[#290BD9] text-white hover:bg-[#2309C5]"
            disabled={submitting}
            data-testid="radius-submit-button"
          >
            {submitting ? 'Saving...' : submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
}
